import { AlertTriangle, CheckCircle2, ExternalLink } from 'lucide-react';
import { useCallback, useContext, useEffect, useRef, useState, isValidElement, cloneElement } from 'react';

import { useFileUpload } from '../hooks/use-file-upload';
import { useToolResponse } from '../hooks/use-tool-response';
import { formatRelativeTime } from '../lib/format-relative-time';
import { AuthContext, useApiClient } from '../providers/auth-provider';

import type { PlanResponse } from '../lib/api-client';

import { Badge } from './Badge';
import { ConfirmationWidget } from './ConfirmationWidget';
import { FileHandlerWidget } from './FileHandlerWidget';
import { GhostTrace } from './GhostTrace';
import { MessageBubble } from './MessageBubble';
import { PlanApprovalCard } from './PlanApprovalCard';
import { SecretsApprovalWidget } from './SecretsApprovalWidget';
import { Spinner } from './Spinner';
import { StatusUpdateCard } from './StatusUpdateCard';
import { TaskCard } from './TaskCard';
import { ThinkingCap } from './ThinkingCap';

import type { BubbleGroupPosition } from './MessageBubble';
import type { ThinkingCapTrace } from './ThinkingCap';

export interface ContentBlockDeltaEvent {
  type: 'content_block_delta';
  delta: string;
  parent_tool_use_id?: string;
}

export interface ContentBlockStartEvent {
  type: 'content_block_start';
  tool_call_id: string;
  tool_name: string;
  tool_input?: Record<string, unknown>;
  /** ID of the parent Task tool call that spawned this tool (sub-agent context) */
  parent_tool_use_id?: string;
}

export interface ToolResultEvent {
  type: 'tool_result';
  tool_call_id: string;
  output: string;
  result?: Record<string, unknown>;
}

export interface PlanApprovalRequestedEvent {
  type: 'plan_approval_requested';
  plan_id: string;
  task_id: string;
  title: string;
  steps: string[];
  data_access: Array<{
    system: string;
    data: string;
    access_level: string;
    expiry: string;
  }>;
  requires_mfa: boolean;
}

export interface StatusEvent {
  type: 'status';
  status: 'typing' | 'thinking' | 'working' | 'reconnecting' | 'reconnected' | 'extended_session';
  message?: string;
}

export interface ErrorEvent {
  type: 'error';
  message: string;
  recoverable: boolean;
  recovery_hint?: string;
}

export interface MessageStopEvent {
  type: 'message_stop';
}

export interface TaskCreatedEvent {
  type: 'task_created';
  task_id: string;
  conversation_id: string;
  title: string;
  description?: string;
}

export interface TaskStatusUpdateEvent {
  type: 'task_status_update';
  task_id: string;
  stage: string;
  phase?: string;
  title: string;
  timestamp: string;
}

export type StreamEventType =
  | 'content_block_delta'
  | 'content_block_start'
  | 'tool_result'
  | 'plan_approval_requested'
  | 'status'
  | 'error'
  | 'message_stop'
  | 'task_created'
  | 'task_status_update';

export type StreamEvent =
  | ContentBlockDeltaEvent
  | ContentBlockStartEvent
  | ToolResultEvent
  | PlanApprovalRequestedEvent
  | StatusEvent
  | ErrorEvent
  | MessageStopEvent
  | TaskCreatedEvent
  | TaskStatusUpdateEvent;

export type ToolRegistryEntry = React.ComponentType<{ event: ToolResultEvent }>;

function TaskCardAdapter({ event }: { event: ToolResultEvent }) {
  const result = event.result ?? {};
  return (
    <TaskCard
      title={(result.title as string) ?? event.output}
      description={(result.description as string) ?? ''}
      stage={(result.stage as 'not-started' | 'awaiting-input' | 'working' | 'complete') ?? 'not-started'}
    />
  );
}

function StatusUpdateCardPlaceholder({ event }: { event: ToolResultEvent }) {
  return (
    <div className="flex items-center gap-sd-2 p-sd-2 rounded-sd-md bg-sd-surface-elevated">
      <Badge variant="working">Status</Badge>
      <span className="text-sd-sm text-sd-text-secondary">{event.output}</span>
    </div>
  );
}

function mapHookStatusToWidgetStatus(
  hookStatus: 'idle' | 'loading' | 'success' | 'error',
  approved: boolean | undefined,
): 'pending' | 'approved' | 'denied' {
  if (hookStatus === 'success') return approved ? 'approved' : 'denied';
  return 'pending';
}

/** Resolved plan statuses that should display as "approved" in the UI. */
const PLAN_APPROVED_STATUSES = ['approved', 'executing', 'completed'];
/** Resolved plan statuses that should display as "denied" in the UI. */
const PLAN_DENIED_STATUSES = ['denied', 'revised'];

/**
 * Hook to fetch a plan's current status from the API.
 * Returns 'loading' while in-flight, 'error' on failure (with a retry function),
 * and the resolved display status on success.
 *
 * Gracefully handles missing AuthProvider (e.g. Storybook, tests, live streaming
 * without auth context) by defaulting to 'pending'.
 */
function usePlanStatus(planId: string | undefined) {
  const authCtx = useContext(AuthContext);
  const apiClient = authCtx?.apiClient ?? null;
  const [status, setStatus] = useState<'loading' | 'pending' | 'approved' | 'denied' | 'error'>(
    apiClient && planId ? 'loading' : 'pending',
  );
  const [plan, setPlan] = useState<PlanResponse | null>(null);

  const fetchStatus = useCallback(() => {
    if (!planId || !apiClient) { setStatus('pending'); return; }
    setStatus('loading');
    apiClient.getPlan(planId).then((p) => {
      setPlan(p);
      if (PLAN_APPROVED_STATUSES.includes(p.status)) setStatus('approved');
      else if (PLAN_DENIED_STATUSES.includes(p.status)) setStatus('denied');
      else setStatus('pending');
    }).catch((err: unknown) => {
      console.warn('[usePlanStatus] getPlan failed:', planId, err);
      setStatus('error');
    });
  }, [apiClient, planId]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  return { status, plan, retry: fetchStatus };
}

function PlanApprovalCardAdapter({ event }: { event: ToolResultEvent }) {
  const { submit, status } = useToolResponse();
  const approvedRef = useRef<boolean | undefined>(undefined);
  const result = event.result ?? {};
  const dataAccess = Array.isArray(result.data_access)
    ? (result.data_access as Array<Record<string, string>>).map((item) => ({
        system: item.system ?? '',
        data: item.data ?? '',
        accessLevel: (item.access_level ?? 'read') as 'read' | 'read-write',
        expiry: item.expiry ?? '',
      }))
    : [];

  return (
    <PlanApprovalCard
      title={(result.title as string) ?? event.output}
      steps={Array.isArray(result.steps) ? (result.steps as string[]) : []}
      dataAccess={dataAccess}
      mfaRequired={(result.requires_mfa as boolean) ?? false}
      status={mapHookStatusToWidgetStatus(status, approvedRef.current)}
      onApprove={() => { approvedRef.current = true; submit(event.tool_call_id, { approved: true }); }}
      onCancel={() => { approvedRef.current = false; submit(event.tool_call_id, { approved: false }); }}
    />
  );
}

function SecretsApprovalAdapter({ event }: { event: ToolResultEvent }) {
  const { submit, status } = useToolResponse();
  const approvedRef = useRef<boolean | undefined>(undefined);
  const result = event.result ?? {};
  const apiRequests = Array.isArray(result.api_requests)
    ? (result.api_requests as Array<Record<string, string>>).map((item) => ({
        system: item.system ?? "",
        permission: (item.permission ?? "read") as "read" | "read-write",
        expiry: item.expiry ?? "",
      }))
    : [];
  return (
    <SecretsApprovalWidget
      summary={(result.summary as string) ?? event.output}
      apiRequests={apiRequests}
      justification={(result.justification as string) ?? ""}
      status={mapHookStatusToWidgetStatus(status, approvedRef.current)}
      onApprove={() => { approvedRef.current = true; submit(event.tool_call_id, { approved: true }); }}
      onDeny={() => { approvedRef.current = false; submit(event.tool_call_id, { approved: false }); }}
    />
  );
}

function mapUploadStatusToWidgetStatus(
  hookStatus: 'idle' | 'uploading' | 'success' | 'error',
): 'prompt' | 'uploading' | 'complete' | 'error' {
  if (hookStatus === 'uploading') return 'uploading';
  if (hookStatus === 'success') return 'complete';
  if (hookStatus === 'error') return 'error';
  return 'prompt';
}

function FileHandlerAdapter({ event, conversationId }: { event: ToolResultEvent; conversationId?: string }) {
  const { uploadFile, status: uploadStatus, error } = useFileUpload(conversationId ?? '');
  const lastFileRef = useRef<File | null>(null);
  const result = event.result ?? {};
  const mode = (result.mode as "upload" | "download") ?? "upload";
  const downloadUrl = (result.download_url as string) ?? undefined;
  const widgetStatus = mode === "download" ? "available" : mapUploadStatusToWidgetStatus(uploadStatus);
  return (
    <FileHandlerWidget
      mode={mode}
      fileName={(result.file_name as string) ?? undefined}
      fileSize={(result.file_size as string) ?? undefined}
      acceptedTypes={Array.isArray(result.accepted_types) ? (result.accepted_types as string[]) : undefined}
      status={widgetStatus}
      errorMessage={mode === "upload" && error ? error : undefined}
      onFileSelect={mode === "upload" ? (file) => { lastFileRef.current = file; void uploadFile(file); } : undefined}
      onRetry={mode === "upload" && uploadStatus === "error" && lastFileRef.current ? () => { void uploadFile(lastFileRef.current!); } : undefined}
      onDownload={mode === "download" && downloadUrl ? () => window.open(downloadUrl, '_blank') : undefined}
    />
  );
}

/**
 * Adapter for `mcp__shiftagent_platform__create_plan` tool results.
 * Renders the PlanApprovalCard and calls the real plan approve/deny API
 * (instead of submitting a tool response back to the agent).
 * After approval/denial, polls for new messages so the trigger response appears.
 */
function CreatePlanAdapter({ event, onPlanApproved }: { event: ToolResultEvent; onPlanApproved?: ((planId: string) => void) | undefined }) {
  const apiClient = useApiClient();
  const result = event.result ?? {};
  const planId = result.plan_id as string | undefined;

  // Check plan's current status from API (handles reload of past conversations)
  const { status, plan, retry } = usePlanStatus(planId);
  // Local override for optimistic UI during approve/deny actions
  const [actionStatus, setActionStatus] = useState<'pending' | 'approved' | 'denied' | null>(null);
  const displayStatus = actionStatus ?? status;

  const steps = Array.isArray(result.steps)
    ? (result.steps as Array<Record<string, string>>).map((s) => s.description ?? s.action ?? JSON.stringify(s))
    : [];
  const dataAccess = Array.isArray(result.data_access)
    ? (result.data_access as Array<Record<string, string>>).map((item) => ({
        system: item.service_name ?? item.system ?? '',
        data: item.reason ?? item.data ?? '',
        accessLevel: (item.access_level ?? 'read') as 'read' | 'read-write',
        expiry: item.expiry ?? '1d',
      }))
    : [];

  const handleApprove = useCallback(async () => {
    if (!planId) return;
    setActionStatus('approved');
    try {
      const response = await apiClient.approvePlan(planId);
      if (response.trigger_ready) {
        onPlanApproved?.(planId);
      }
    } catch {
      setActionStatus(null); // Revert on failure — fall back to API status
    }
  }, [apiClient, planId, onPlanApproved]);

  const handleDeny = useCallback(async () => {
    if (!planId) return;
    setActionStatus('denied');
    try {
      await apiClient.denyPlan(planId);
    } catch {
      setActionStatus(null);
    }
  }, [apiClient, planId]);

  return (
    <PlanApprovalCard
      title={(result.summary as string) ?? (result.title as string) ?? 'Plan approval required'}
      steps={steps}
      dataAccess={dataAccess}
      mfaRequired={false}
      status={displayStatus}
      approvedBy={plan?.approved_by ?? null}
      approvedAt={plan?.approved_at ?? null}
      denialReason={plan?.denial_reason ?? null}
      onApprove={() => { void handleApprove(); }}
      onCancel={() => { void handleDeny(); }}
      onRetry={retry}
    />
  );
}

/**
 * Adapter for `plan_approval_requested` stream events.
 * Unlike CreatePlanAdapter (which handles tool results), this renders from
 * the custom plan_approval_requested event type. It uses usePlanStatus to
 * resolve the plan's current state on reload.
 */
function PlanApprovalRequestedAdapter({ event, onApprove, onCancel }: {
  event: PlanApprovalRequestedEvent;
  onApprove?: ((planId: string) => void) | undefined;
  onCancel?: ((planId: string) => void) | undefined;
}) {
  const { status, plan, retry } = usePlanStatus(event.plan_id);
  return (
    <PlanApprovalCard
      title={event.title}
      steps={event.steps}
      dataAccess={event.data_access.map((item) => ({
        system: item.system,
        data: item.data,
        accessLevel: item.access_level as 'read' | 'read-write',
        expiry: item.expiry,
      }))}
      mfaRequired={event.requires_mfa}
      status={status}
      approvedBy={plan?.approved_by ?? null}
      approvedAt={plan?.approved_at ?? null}
      denialReason={plan?.denial_reason ?? null}
      onApprove={() => onApprove?.(event.plan_id)}
      onCancel={() => onCancel?.(event.plan_id)}
      onRetry={retry}
    />
  );
}

function ConfirmationAdapter({ event }: { event: ToolResultEvent }) {
  const { submit, status } = useToolResponse();
  const selectedRef = useRef<string | undefined>(undefined);
  const result = event.result ?? {};
  return (
    <ConfirmationWidget
      question={(result.question as string) ?? event.output}
      options={Array.isArray(result.options) ? (result.options as string[]) : undefined}
      selectedOption={status === 'success' ? selectedRef.current : undefined}
      onSelect={(option) => {
        selectedRef.current = option;
        submit(event.tool_call_id, { selected: option });
      }}
    />
  );
}

export const DEFAULT_TOOL_REGISTRY: Record<string, ToolRegistryEntry> = {
  create_task: TaskCardAdapter,
  get_task_status: StatusUpdateCardPlaceholder,
  secrets_manager: PlanApprovalCardAdapter,
  secrets_request: SecretsApprovalAdapter,
  file_upload: FileHandlerAdapter,
  file_download: FileHandlerAdapter,
  confirmation: ConfirmationAdapter,
};

/** Map internal tool names to lowercase past-tense verbs for ghost traces */
export function formatToolVerb(toolName: string): string {
  switch (toolName) {
    case 'Bash': return 'ran';
    case 'Task': return 'delegated';
    case 'Glob': return 'searched files';
    case 'Read': return 'read';
    case 'Write': return 'wrote';
    case 'Edit': return 'edited';
    case 'Grep': return 'searched';
    case 'WebSearch': return 'searched web';
    case 'WebFetch': return 'fetched';
    default: return `used ${toolName}`;
  }
}

/** Extract the most relevant input string for a tool call's IN section */
function formatToolInput(toolName: string, toolInput?: Record<string, unknown>): string {
  if (!toolInput) return '';
  switch (toolName) {
    case 'Bash':
      return (toolInput.command as string) ?? '';
    case 'Read':
    case 'Write':
      return (toolInput.file_path as string) ?? '';
    case 'Edit':
      return (toolInput.file_path as string) ?? '';
    case 'Grep':
      return [toolInput.pattern, toolInput.path].filter(Boolean).join(' in ');
    case 'Glob':
      return (toolInput.pattern as string) ?? '';
    case 'WebSearch':
      return (toolInput.query as string) ?? '';
    case 'WebFetch':
      return (toolInput.url as string) ?? '';
    case 'Task':
      return (toolInput.prompt as string) ?? (toolInput.description as string) ?? '';
    default: {
      // For unknown tools, show a compact JSON of the input
      try {
        return JSON.stringify(toolInput, null, 2);
      } catch {
        return '';
      }
    }
  }
}

/** Extract the last segment of a file path (filename or folder name) */
export function basename(filePath: string): string {
  const trimmed = filePath.replace(/\/+$/, '');
  return trimmed.split('/').pop() ?? trimmed;
}

/** Extract a short description for the tool card header line */
function formatToolDescription(toolName: string, toolInput?: Record<string, unknown>): string {
  if (!toolInput) return '';
  switch (toolName) {
    case 'Bash':
      return (toolInput.description as string) ?? '';
    case 'Read':
    case 'Write':
    case 'Edit':
      return basename((toolInput.file_path as string) ?? '');
    case 'Grep':
      return (toolInput.pattern as string) ?? '';
    case 'Glob':
      return (toolInput.pattern as string) ?? '';
    case 'WebSearch':
      return (toolInput.query as string) ?? '';
    case 'WebFetch':
      return (toolInput.url as string) ?? '';
    case 'Task':
      return (toolInput.description as string) ?? '';
    default:
      return '';
  }
}

export interface StreamRendererProps {
  events: StreamEvent[];
  toolRegistry?: Record<string, ToolRegistryEntry>;
  onApprove?: (planId: string, mfaCode?: string) => void;
  onCancel?: (planId: string) => void;
  onTaskClick?: (taskData: Record<string, unknown>) => void;
  onTaskEvent?: (event: TaskCreatedEvent | TaskStatusUpdateEvent) => void;
  /** Called after plan approval so the parent can send the streaming trigger */
  onPlanApproved?: (planId: string) => void;
  /** Collapse raw tool calls into a "Thinking Cap" ticker instead of individual ghost traces */
  thinkingCap?: boolean;
  conversationId?: string;
  timestamp?: string;
  className?: string;
}

/** A tagged block entry for the two-pass rendering pipeline. */
type BlockEntry =
  | { kind: 'assistant-bubble'; key: string; text: string }
  | { kind: 'other'; key: string; node: React.ReactNode };

/**
 * Assign groupPosition to each assistant bubble in the entries array.
 *
 * All assistant bubbles within a single StreamRenderer are from the same turn,
 * so they always form one connected group (regardless of ghost traces or other
 * elements between them). A single bubble stays 'solo'.
 */
function assignGroupPositions(entries: BlockEntry[]): BubbleGroupPosition[] {
  const positions: BubbleGroupPosition[] = new Array(entries.length).fill('solo');

  const bubbleIndices: number[] = [];
  for (let i = 0; i < entries.length; i++) {
    if (entries[i]!.kind === 'assistant-bubble') bubbleIndices.push(i);
  }

  if (bubbleIndices.length <= 1) return positions;

  // All assistant bubbles form one group
  positions[bubbleIndices[0]!] = 'first';
  for (let i = 1; i < bubbleIndices.length - 1; i++) {
    positions[bubbleIndices[i]!] = 'middle';
  }
  positions[bubbleIndices[bubbleIndices.length - 1]!] = 'last';

  return positions;
}

export function StreamRenderer({ events, toolRegistry, onApprove, onCancel, onTaskEvent, onPlanApproved, thinkingCap = false, conversationId, timestamp, className = '' }: StreamRendererProps) {
  const conversationIdRef = useRef(conversationId);
  conversationIdRef.current = conversationId;
  const onPlanApprovedRef = useRef(onPlanApproved);
  onPlanApprovedRef.current = onPlanApproved;
  const fileHandlerWithConversation = useRef<ToolRegistryEntry>(({ event }) => (
    <FileHandlerAdapter event={event} conversationId={conversationIdRef.current} />
  )).current;
  const createPlanWithAction = useRef<ToolRegistryEntry>(({ event }) => (
    <CreatePlanAdapter event={event} onPlanApproved={onPlanApprovedRef.current ? (planId: string) => onPlanApprovedRef.current?.(planId) : undefined} />
  )).current;
  const registry = {
    ...DEFAULT_TOOL_REGISTRY,
    file_upload: fileHandlerWithConversation,
    file_download: fileHandlerWithConversation,
    mcp__shiftagent_platform__create_plan: createPlanWithAction,
    ...toolRegistry,
  };
  const entries: BlockEntry[] = [];
  let accumulatedText = '';
  // Accumulate consecutive compact tool cards into a tight group
  let accumulatedToolCards: React.ReactNode[] = [];
  // Thinking cap mode: accumulate trace data instead of JSX nodes
  let accumulatedTraces: ThinkingCapTrace[] = [];
  let thinkingCapSegment = 0;
  let blockIndex = 0;
  let isDone = false;

  const toolOutputMap = new Map<string, ToolResultEvent>();
  for (const event of events) {
    if (event.type === 'tool_result') {
      toolOutputMap.set(event.tool_call_id, event);
    }
  }

  function flushText() {
    if (accumulatedText) {
      flushToolCards();
      entries.push({
        kind: 'assistant-bubble',
        key: `text-${blockIndex++}`,
        text: accumulatedText,
      });
      accumulatedText = '';
    }
  }

  function flushToolCards() {
    if (thinkingCap) {
      flushThinkingCap();
      return;
    }
    if (accumulatedToolCards.length > 0) {
      entries.push({
        kind: 'other',
        key: `toolgroup-${blockIndex++}`,
        node: (
          <div className="flex flex-col gap-0 -mt-1 sd-fade-in" data-testid="ghost-trace-group">
            {accumulatedToolCards}
          </div>
        ),
      });
      accumulatedToolCards = [];
    }
  }

  function flushThinkingCap() {
    if (accumulatedTraces.length === 0) return;
    const traces = [...accumulatedTraces];
    const isActive = traces.some((t) => t.loading);
    const segKey = `thinking-cap-${thinkingCapSegment}`;
    entries.push({
      kind: 'other',
      key: segKey,
      node: (
        <div className="-mt-1 sd-fade-in">
          <ThinkingCap traces={traces} active={isActive} />
        </div>
      ),
    });
    accumulatedTraces = [];
    thinkingCapSegment++;
  }

  // Keep only the LAST create_plan card per message — demote earlier ones to ghost traces.
  // When the agent retries plan creation, only the final attempt should show as a full card.
  const planToolCallIds = events
    .filter((e): e is ContentBlockStartEvent =>
      e.type === 'content_block_start' &&
      (e as ContentBlockStartEvent).tool_name === 'mcp__shiftagent_platform__create_plan')
    .map((e) => e.tool_call_id);
  const demotedPlanIds = new Set(planToolCallIds.slice(0, -1));

  for (const event of events) {
    if (isDone) break;

    switch (event.type) {
      case 'content_block_delta': {
        accumulatedText += event.delta;
        break;
      }
      case 'content_block_start': {
        flushText();
        // Demote earlier create_plan calls to ghost traces — only show the last one as a full card
        if (demotedPlanIds.has(event.tool_call_id)) {
          if (thinkingCap) {
            accumulatedTraces.push({
              id: event.tool_call_id,
              verb: 'created plan',
              loading: false,
              success: true,
            });
          } else {
            accumulatedToolCards.push(
              <GhostTrace
                key={`tool-${blockIndex++}`}
                verb="created plan"
                loading={false}
                success={true}
              />
            );
          }
          break;
        }
        const output = toolOutputMap.get(event.tool_call_id);
        const RegistryComponent = output ? registry[event.tool_name] : undefined;
        if (output && RegistryComponent) {
          // Custom registry component (TaskCard, PlanApprovalCard, etc.) — flush tool group first
          flushToolCards();
          entries.push({
            kind: 'other',
            key: `tool-${blockIndex++}`,
            node: (
              <div className="sd-fade-in sd-slide-up motion-reduce:animate-none">
                <RegistryComponent event={output} />
              </div>
            ),
          });
        } else if (thinkingCap) {
          // Thinking cap mode — accumulate trace data
          const inputText = formatToolInput(event.tool_name, event.tool_input);
          const descText = formatToolDescription(event.tool_name, event.tool_input);
          accumulatedTraces.push({
            id: event.tool_call_id,
            verb: formatToolVerb(event.tool_name),
            description: descText || undefined,
            input: inputText || undefined,
            output: output?.output,
            loading: !output,
            success: !!output,
          });
        } else {
          // Ghost trace — whisper-line tool call
          const inputText = formatToolInput(event.tool_name, event.tool_input);
          const descText = formatToolDescription(event.tool_name, event.tool_input);
          accumulatedToolCards.push(
            <GhostTrace
              key={`tool-${blockIndex++}`}
              verb={formatToolVerb(event.tool_name)}
              {...(descText ? { description: descText } : {})}
              {...(inputText ? { input: inputText } : {})}
              {...(output ? { output: output.output } : {})}
              loading={!output}
              success={!!output}
            />
          );
        }
        break;
      }
      case 'tool_result': {
        break;
      }
      case 'plan_approval_requested': {
        flushText();
        entries.push({
          kind: 'other',
          key: `plan-${blockIndex++}`,
          node: (
            <div className="sd-fade-in">
              <PlanApprovalRequestedAdapter
                event={event}
                onApprove={onApprove}
                onCancel={onCancel}
              />
            </div>
          ),
        });
        break;
      }
      case 'status': {
        flushText();
        const isCompleted = event.status === 'reconnected';
        entries.push({
          kind: 'other',
          key: `status-${blockIndex++}`,
          node: (
            <div className="sd-fade-in">
              <div className="flex items-center gap-sd-2 px-sd-3 py-sd-2 rounded-sd-lg bg-sd-surface border border-sd-border/30 shadow-sd-sm">
                {isCompleted ? (
                  <CheckCircle2 className="w-4 h-4 text-sd-success shrink-0" />
                ) : (
                  <Spinner size="sm" />
                )}
                <span className="text-sd-sm text-sd-text-secondary font-medium">
                  {event.status === 'reconnecting'
                    ? 'Re-establishing session...'
                    : event.status === 'reconnected'
                      ? 'Session reestablished'
                      : event.status === 'extended_session'
                        ? (event.message ?? 'Agent is still working in the background. Results will appear here when ready.')
                        : `Agent is ${event.status === 'typing' ? 'working' : event.status}...`}
                </span>
              </div>
            </div>
          ),
        });
        break;
      }
      case 'error': {
        flushText();
        entries.push({
          kind: 'other',
          key: `error-${blockIndex++}`,
          node: (
            <div className="sd-fade-in">
              <div role="alert" className="flex items-start gap-sd-2 p-sd-2 rounded-sd-md bg-sd-error/10 shadow-sd-sm border border-sd-error/20 transition-colors duration-200">
                <AlertTriangle className="w-4 h-4 text-sd-error shrink-0 mt-0.5" aria-hidden="true" />
                <div>
                  <p className="text-sd-sm text-sd-text">{event.message}</p>
                  {event.recovery_hint && (
                    event.recovery_hint.startsWith('https://') ? (
                      <a
                        href={event.recovery_hint}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sd-xs text-sd-link mt-sd-1 inline-flex items-center gap-1 hover:underline"
                      >
                        {event.recovery_hint}
                        <ExternalLink className="w-3 h-3" aria-hidden="true" />
                      </a>
                    ) : (
                      <p className="text-sd-xs text-sd-text-muted mt-sd-1">{event.recovery_hint}</p>
                    )
                  )}
                </div>
              </div>
            </div>
          ),
        });
        break;
      }
      case 'task_created': {
        flushText();
        onTaskEvent?.(event);
        entries.push({
          kind: 'other',
          key: `task-${blockIndex++}`,
          node: (
            <div className="sd-fade-in">
              <TaskCard
                title={event.title}
                description={event.description ?? ''}
                stage="not-started"
              />
            </div>
          ),
        });
        break;
      }
      case 'task_status_update': {
        flushText();
        onTaskEvent?.(event);
        const stageMap: Record<string, 'not-started' | 'awaiting-input' | 'working' | 'complete'> = {
          not_started: 'not-started',
          awaiting_input: 'awaiting-input',
          working: 'working',
          complete: 'complete',
        };
        const mappedStage = stageMap[event.stage] ?? 'working';
        entries.push({
          kind: 'other',
          key: `status-update-${blockIndex++}`,
          node: (
            <div className="sd-fade-in">
              <StatusUpdateCard
                title={event.title}
                stage={mappedStage}
                message={event.phase ? `Phase: ${event.phase} — ${event.timestamp}` : event.timestamp}
              />
            </div>
          ),
        });
        break;
      }
      case 'message_stop': {
        flushText();
        isDone = true;
        break;
      }
    }
  }

  flushText();
  flushToolCards();

  // Second pass: compute group positions and render
  const groupPositions = assignGroupPositions(entries);
  const blocks: React.ReactNode[] = entries.map((entry, i) => {
    if (entry.kind === 'assistant-bubble') {
      const pos = groupPositions[i] ?? 'solo';
      // Tighten gap for continuation bubbles within a group
      const tighten = pos === 'middle' || pos === 'last' ? ' -mt-1' : '';
      return (
        <div key={entry.key} className={`sd-fade-in${tighten}`}>
          <MessageBubble role="assistant" content={entry.text} groupPosition={pos} />
        </div>
      );
    }
    // 'other' entries already have their own wrapper — clone with key to avoid extra DOM layer
    return isValidElement(entry.node) ? cloneElement(entry.node, { key: entry.key }) : entry.node;
  });

  return (
    <div aria-live="polite" className={`flex flex-col gap-sd-2 ${className}`.trim()}>
      {blocks}
      {timestamp && <div className="text-sd-xs text-sd-text-muted opacity-70">{formatRelativeTime(timestamp)}</div>}
    </div>
  );
}
